import { useEffect, useState } from "react";
import { Bell, Check, Loader2, Plus, Trash2, Send, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const RULE_DEFS = [
    { type: "volume_drop", title: "Volume drop vs yesterday",
      desc: "Alert when today's volume falls below yesterday's by this many %.",
      fields: [{ key: "threshold_pct", label: "Threshold %", type: "number", min: 5, max: 100, default: 50 }] },
    { type: "failures_exceed_success", title: "Failures spiking",
      desc: "Alert when failures > successes today (with a minimum txn count to filter noise).",
      fields: [{ key: "threshold_txns", label: "Min txns today", type: "number", min: 1, max: 1000, default: 10 }] },
    { type: "dns_pending", title: "DNS verification pending",
      desc: "Alert when any payment link's custom domain is awaiting DNS verification.",
      fields: [] },
];

export default function AlertsConfigPage({ scope = "admin", path = "/admin/alerts" }) {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const load = async () => {
        const r = await api.get(`${path}/config`);
        setCfg({
            channels: r.data.channels || [],
            rules: (RULE_DEFS).map((d) => {
                const existing = (r.data.rules || []).find((x) => x.type === d.type);
                return existing || {
                    type: d.type, enabled: true, min_minutes: 30,
                    ...(d.fields.reduce((acc, f) => ({ ...acc, [f.key]: f.default }), {})),
                };
            }),
        });
    };
    useEffect(() => { load();   }, [path]);

    const save = async () => {
        setBusy(true); setMsg("");
        try { await api.patch(`${path}/config`, cfg); setMsg("Saved."); }
        catch (e) { setMsg(e?.response?.data?.detail || e.message); }
        finally { setBusy(false); setTimeout(() => setMsg(""), 2000); }
    };

    const test = async () => {
        setBusy(true); setMsg("");
        try {
            const r = await api.post(`${path}/test`);
            const results = Object.entries(r.data.sent || {});
            if (results.length === 0) setMsg("No channels configured. Add one above.");
            else setMsg(results.map(([k, ok]) => `${ok ? "✓" : "✗"} ${k}`).join(" · "));
        } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
        finally { setBusy(false); }
    };

    if (!cfg) return <div className="text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />Loading…</div>;

    const upsertChannel = (i, patch) => {
        const next = [...cfg.channels]; next[i] = { ...next[i], ...patch }; setCfg({ ...cfg, channels: next });
    };
    const addChannel = (type) => setCfg({ ...cfg, channels: [...cfg.channels,
        type === "slack" ? { type, webhook_url: "", label: "", enabled: true }
                         : { type: "telegram", bot_token: "", chat_id: "", label: "", enabled: true }] });
    const removeChannel = (i) => setCfg({ ...cfg, channels: cfg.channels.filter((_, k) => k !== i) });

    const upsertRule = (i, patch) => {
        const next = [...cfg.rules]; next[i] = { ...next[i], ...patch }; setCfg({ ...cfg, rules: next });
    };

    return (
        <div className="space-y-5 max-w-3xl">
            <div>
                <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                    <Bell className="w-5 h-5 text-fuchsia-700" /> Alert Notifications
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Get pinged on Slack or Telegram when {scope === "admin" ? "platform" : "your"} health rules fire.
                    Each rule has a grace period — the alert only fires if the issue persists.
                </p>
            </div>

            <div className="card p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="font-semibold">Channels</h2>
                    <div className="flex gap-2">
                        <button data-testid="add-slack" onClick={() => addChannel("slack")}
                                className="btn-secondary px-2.5 py-1 rounded-md text-xs flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Slack
                        </button>
                        <button data-testid="add-telegram" onClick={() => addChannel("telegram")}
                                className="btn-secondary px-2.5 py-1 rounded-md text-xs flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Telegram
                        </button>
                    </div>
                </div>
                {cfg.channels.length === 0 && <div className="text-sm text-slate-400 text-center py-4">No channels yet — add one above.</div>}
                <div className="space-y-2">
                    {cfg.channels.map((c, i) => (
                        // Stable composite key: type+label survives reorder/delete
                        // far better than the array index (which caused stale
                        // form state when a middle channel was removed).
                        <div key={`${c.type}-${c.label || ""}-${i}`} data-testid={`ch-${c.type}-${i}`} className="border border-slate-200 rounded-md p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <span className={`pill ${c.type === "slack" ? "pill-info" : "pill-pending"}`}>{c.type}</span>
                                <input className="input flex-1 text-xs" placeholder="Label (e.g. ops)"
                                       value={c.label || ""} onChange={(e) => upsertChannel(i, { label: e.target.value })} />
                                <label className="flex items-center gap-1 text-xs">
                                    <input type="checkbox" checked={!!c.enabled}
                                           onChange={(e) => upsertChannel(i, { enabled: e.target.checked })} />
                                    enabled
                                </label>
                                <button onClick={() => removeChannel(i)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            {c.type === "slack" ? (
                                <input className="input font-mono text-xs"
                                       placeholder="https://hooks.slack.com/services/T.../B.../..."
                                       value={c.webhook_url || ""}
                                       onChange={(e) => upsertChannel(i, { webhook_url: e.target.value })} />
                            ) : (
                                <div className="grid grid-cols-2 gap-2">
                                    <input className="input font-mono text-xs"
                                           placeholder="Bot token (1234:ABC...)"
                                           value={c.bot_token || ""}
                                           onChange={(e) => upsertChannel(i, { bot_token: e.target.value })} />
                                    <input className="input font-mono text-xs"
                                           placeholder="Chat ID (e.g. -1001234)"
                                           value={c.chat_id || ""}
                                           onChange={(e) => upsertChannel(i, { chat_id: e.target.value })} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="card p-5 space-y-3">
                <h2 className="font-semibold">Rules</h2>
                {cfg.rules.map((r, i) => {
                    const def = RULE_DEFS.find((d) => d.type === r.type);
                    if (!def) return null;
                    return (
                        <div key={r.type} data-testid={`rule-${r.type}`} className="border border-slate-200 rounded-md p-3">
                            <label className="flex items-start gap-3">
                                <input type="checkbox" checked={!!r.enabled} className="mt-1"
                                       onChange={(e) => upsertRule(i, { enabled: e.target.checked })} />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-sm">{def.title}</span>
                                        {r.enabled && <span className="pill pill-success">on</span>}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5 mb-2">{def.desc}</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {def.fields.map((f) => (
                                            <div key={f.key}>
                                                <label className="label">{f.label}</label>
                                                <input type={f.type} min={f.min} max={f.max}
                                                       className="input"
                                                       data-testid={`rule-${r.type}-${f.key}`}
                                                       value={r[f.key] ?? ""}
                                                       onChange={(e) => upsertRule(i, { [f.key]: Number(e.target.value) })} />
                                            </div>
                                        ))}
                                        <div>
                                            <label className="label">Grace period (minutes)</label>
                                            <input type="number" min={0} max={1440}
                                                   className="input"
                                                   data-testid={`rule-${r.type}-min-minutes`}
                                                   value={r.min_minutes ?? 30}
                                                   onChange={(e) => upsertRule(i, { min_minutes: Number(e.target.value) })} />
                                            <div className="text-[10px] text-slate-400 mt-0.5">
                                                Alert only fires if the issue persists for this many minutes.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </label>
                        </div>
                    );
                })}
            </div>

            {msg && (
                <div className="text-xs px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-800 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5" /> {msg}
                </div>
            )}

            <div className="flex gap-2 justify-end">
                <button data-testid="alerts-test" onClick={test} disabled={busy}
                        className="btn-secondary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send test message
                </button>
                <button data-testid="alerts-save" onClick={save} disabled={busy}
                        className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Save changes
                </button>
            </div>
        </div>
    );
}
